class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');

    this.setupEventListeners();
  }

  setupEventListeners() {
    const searchForm = this.querySelector('form.search');
    if (searchForm) searchForm.addEventListener('submit', this.onFormSubmit.bind(this));

    const closeButton = this.querySelector('button[type="button"]');
    if (closeButton) closeButton.addEventListener('click', this.close.bind(this));

    const resetButton = this.querySelector('button[type="reset"]');
    if (resetButton) resetButton.addEventListener('click', this.clear.bind(this));

    // Add click handlers for quick search links
    this.querySelectorAll('.quick-search-link').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const searchTerm = e.currentTarget.getAttribute('data-search-term');
        if (searchTerm) {
          this.input.value = searchTerm;
          this.input.focus();
          this.getSearchResults(searchTerm);
          this.open();
        }
      });
    });
    // ends

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length === 0) {
      this.clear();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    } else {
      // Save search to history before submitting
      this.saveSearch(this.getQuery());
    }
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  } 

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.clear(event);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  saveSearch(query) {
    let searches = JSON.parse(sessionStorage.getItem('searchHistory')) || [];
    if (!searches.includes(query) && query.trim() !== '') {
      searches.unshift(query); // Add to beginning
      searches = searches.slice(0, 10); // Keep last 10 searches
      sessionStorage.setItem('searchHistory', JSON.stringify(searches));
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = ''; 

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) {
      this.saveSearch(this.getQuery()); // Save before clicking
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=${perPage}&section_id=predictive-search`)
      .then((response) => { 
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
        
        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        this.close();
        throw error;
      }); 
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);  

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    document.body.classList.add('predictive-search--focus');
  }

  close() {
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    event.preventDefault();
    
    this.input.value = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.focus();

    if (this.modal) this.modal.classList.remove('searching');
  }
}

customElements.define('predictive-search', PredictiveSearch);


class SearchHistory extends HTMLElement {
  constructor() {
    super();
    this.storageKey = this.dataset.storageKey || 'searchHistory';
    this.maxItems = Number(this.dataset.maxItems || 10);
    this.renderLimit = Number(this.dataset.renderLimit || 6);
  }

  connectedCallback() {
    this.render();
  }

  getHistory() {
    return JSON.parse(sessionStorage.getItem(this.storageKey)) || [];
  }

  render() {
    const history = this.getHistory().slice(0, this.renderLimit);
    const historyWrap = this.closest('.view-history-wrap');

    if (history.length === 0) {
      if (historyWrap) {
        historyWrap.style.display = 'none';
      }
      this.innerHTML = '';
      return;
    }

    if (historyWrap) {
      historyWrap.style.display = '';
    }

    this.innerHTML = history.map(item => `
      <a class="item text-main link h6" href="/search?q=${encodeURIComponent(item)}">
        <span>${item}</span>
        <i class="icon icon-arrow-top-right"></i>
      </a>
    `).join('');
  }
}

customElements.define('search-history', SearchHistory);

class PredictiveSearchType extends PredictiveSearch {

  getSelectedType() {
    const select = document.querySelector('#product_cat');
    return select ? select.value.trim() : '';
  }

  buildFinalQuery() {
    const base = this.getQuery();
    const selectedType = this.getSelectedType();

    if (!base.length) return '';
    if (!selectedType) return base;

    return `product_type:${selectedType} AND ${base}`;
  }

  /* -----------------------------
     Disable Predictive Fetch
  ------------------------------*/
  onChange() {
    return;
  }

  getSearchResults() {
    return;
  }

  /* -----------------------------
     Override Submit → Redirect
  ------------------------------*/
  onFormSubmit(event) {
    event.preventDefault();

    const finalQuery = this.buildFinalQuery();
    if (!finalQuery.length) return;

    this.saveSearch(this.getQuery());

    const searchUrl = `/search?q=${encodeURIComponent(finalQuery)}&type=product`;
    window.location.href = searchUrl;
  }
}

customElements.define('predictive-search-type', PredictiveSearchType);